/**
 * Detection / Why blocked - the landing page for the link in a SQL error.
 *
 * When the DDR gateway blocks a query it answers the client with a synthetic
 * pgwire error whose Hint field carries a URL, so the person whose query just
 * failed can find out why without raising a ticket. The gateway composes that
 * URL as `<console>/operator/why-blocked/why/<request-id>`
 * (apps/edge-detection/src/synthetic_error.rs).
 *
 * Nothing served that path. The console has `/detection/decisions` and no
 * `/operator/...` route at all, so the one link we put in front of a blocked
 * DBA - at a bank, in production, at the moment their query failed - opened a
 * dead page. Found 8 Sep 2026 while auditing the delivered release.
 *
 * Fixing it here rather than in the gateway is deliberate: the console is
 * rebuilt for this release anyway, and this also repairs the link for any
 * gateway already deployed, which a Rust-side change could not do.
 *
 * The decisions page has read `?q=` since 2026-05-26 and filters on request
 * id, so forwarding the id there lands the reader on their own decision rather
 * than on a list they have to search.
 */
export function WhyBlockedRedirect() {
  if (typeof window !== 'undefined') {
    // Last path segment, whatever the router matched. Guarded because a
    // truncated copy-paste of the URL is likelier than not, and an empty id
    // should still land on the decision list rather than throw.
    const parts = window.location.pathname.split('/').filter(Boolean);
    const reqId = parts[parts.length - 1] ?? '';
    const looksLikeAnId = reqId !== '' && reqId !== 'why' && reqId !== 'why-blocked';
    const target = looksLikeAnId
      ? `/detection/decisions?q=${encodeURIComponent(reqId)}`
      : '/detection/decisions';
    // replace(), not assign(): the dead URL should not sit in history behind
    // the reader, where Back would return them to it.
    window.location.replace(target);
  }

  return (
    <div className="p-8 text-sm text-muted-foreground">
      Opening the decision record&hellip;
    </div>
  );
}
